import type { Icon } from "@phosphor-icons/react";
import type { Temporal } from "@/utils/temporal";
import type { DocumentLineage } from "@/components/FileViewer/services/useDocumentMetadata";

// ============= Constants from Database Constraints =============

// From: CREATE DOMAIN entity_kind AS TEXT CHECK (VALUE IN (...))
export type EntityKind =
  | "person"
  | "company"
  | "manager"
  | "vehicle"
  | "topic"
  | "interaction"
  | "client"
  | "portfolio"
  | "deal";

// Aggregation view kinds (not RMS entities — documents live in blobs, not rms_entities)
export type AggregationKind = "document" | "shadowbook";

// Union of all kind strings — used for icon lookups and similar string→value mappings
export type ViewKind = EntityKind | AggregationKind;

// Narrows a table to the records linked to one RMS entity, for the entity
// page's table renderings of its Interactions / Documents tabs (#10552).
// Rides on the ViewConfig so it reaches the search store, the query key and
// the request body through the path they already travel. Both tables scope
// through the same `scope_entity_public_id` request parameter, never by
// pushing onto `SearchState.filters` — that array is user-facing and
// user-removable by design, and a scope a user could delete would show them
// the whole org on an entity's page.
export interface EntityScope {
  publicId: string;
  // Documents table only: widen the scope the way the Documents *list*
  // does — include documents on the entity's related investment vehicles.
  // Absent → the documents the entity reaches itself, matching the list with
  // the toggle off. A file merely attached to one of the entity's
  // interactions is in neither. The interactions table ignores this.
  includeVehicleDocuments?: boolean;
  // Interactions table only: the list's relationship toggle. Filters the
  // scoped set by relationship_kind — participantOnly keeps only primary
  // participants; otherwise the backend returns above-passing rows
  // unconditionally (#10406). The documents table ignores this.
  participantOnly?: boolean;
}

// Entity kinds the documents table can scope to — those with a
// `linked_entities:<kind>` fact in the document schema. Source of truth is
// `_LINKED_ENTITY_DOCUMENT_COLUMNS` in fp-backend/fp/rms/schema/loaders.py;
// `interaction` is deliberately absent there, so a documents table cannot be
// scoped to one.
export const DOCUMENT_SCOPE_KINDS: readonly EntityKind[] = [
  "manager",
  "vehicle",
  "company",
  "person",
  "client",
  "portfolio",
  "topic",
  "deal",
];

// Discriminated union for view configuration
// Which versions of a dataset's rows a grid reads (#12498). A dataset row is a
// version: a value the source restates is kept beside the new one, each with
// when it was pulled. `asOfDay` is a calendar day (YYYY-MM-DD): the rows as the
// source showed them at the end of that day for the viewer.
export type DatasetVersions =
  | { kind: "history" }
  | { kind: "asOfDay"; day: string };

export type ViewConfig =
  | { type: "search"; entityKind: EntityKind; scope?: EntityScope }
  | {
      type: "aggregation";
      panelKind: AggregationKind;
      flat?: boolean;
      scope?: EntityScope;
      // The columns the grid starts from when no shared view supplies its own
      // (#13370, round 32b): a record page's layout's Table columns, by schema
      // name. Absent, the grid seeds as every other view does.
      defaultColumns?: readonly string[];
    }
  | { type: "performance"; asOfDate?: string; timePeriod?: string }
  // The Caissa ledger (#10760, slices 6-7). Unscoped it is the organization-wide
  // view, which is what "net cash flow by portfolio" needs — that question
  // aggregates across funds, and no per-fund tab can answer it. Scoped to an
  // entity it is that vehicle's own Transactions tab, so both surfaces share one
  // grid, one schema and one column layout. Only `publicId` is read; the
  // documents/interactions widening toggles have no analogue on a ledger.
  | { type: "transactions"; scope?: EntityScope }
  // The global Holdings view (#10835). Like transactions it rides the shared
  // grid, unscoped for the org-wide cross-entity view and scoped to an entity
  // for that entity's holdings. Read-only: the grid is a viewer, and editing
  // stays on the per-entity snapshot tab. `asOfDate` (YYYY-MM-DD) drives the
  // point-in-time view — each entity contributes its latest snapshot on or
  // before it; omitted, the backend defaults to the latest snapshot available.
  | { type: "holdings"; scope?: EntityScope; asOfDate?: string }
  // One org-defined dataset (#12178): a name plus a typed column schema declared
  // as data, so a new kind of tabular data is a new view rather than a new view
  // kind. The only parameterised member: the schema and the rows both depend on
  // WHICH dataset, so `datasetId` travels in the schema kind and in the search
  // body. The source fills its columns, which stay read-only; the columns the
  // organization added to it edit (#12733). Its rows are not records, so they
  // link nowhere.
  // `datasetName` is carried for labels only; the id is the identity.
  // Scoped to a record it is that record's tab of the dataset: only the rows
  // whose record cells link to it. `versions` is which versions of the rows
  // the grid reads (#12498): absent, the current ones. It is a way of looking
  // at the view for the session, not part of a saved view, which always opens
  // on the current rows.
  | {
      type: "dataset";
      datasetId: number;
      datasetName?: string;
      scope?: EntityScope;
      versions?: DatasetVersions;
    }
  | { type: "activity" }
  // The unified Bipsync "Document review" view: one editable/filterable/
  // sortable grid over the staged documents AND interactions awaiting sync.
  // Replaces the old Activity-view review-mode toggle. Gated to
  // bipsync-push-enabled orgs. The data fetch hits
  // POST /rms/review/document-review/search and editability is resolved
  // per-cell from each RowCell's provenance override (see cellReadOnly +
  // persistence).
  | { type: "document_review" }
  // A process surface owned by a feature outside the RMS: its rows are the
  // feature's work items, not records, served on the shared search contract
  // and reached from the feature's own page rather than the view strip. The
  // feature describes the view once (`ProcessView`) and the grid reads every
  // view-specific choice from that description.
  | { type: "process"; view: ProcessView }
  // Tasks in the RMS (#11942 round 21). A process surface like Activity:
  // its rows are the organization's tasks (the tasks system's own tables,
  // not records), served by POST /work/tasks/search on the shared search
  // contract with the schema from the bootstrap, so the Table, the Board
  // (lanes = the organization's task statuses), filters, saved views and
  // export are the product's own. Scoped to a record it is that record's
  // Tasks tab.
  | { type: "tasks"; scope?: EntityScope };

// Extract the schema kind string from a ViewConfig (used for API calls like /rms/schema/{kind})
export function viewConfigSchemaKind(config: ViewConfig): string {
  if (config.type === "performance") return "performance";
  if (config.type === "transactions") return "transactions";
  if (config.type === "tasks") return "tasks";
  if (config.type === "holdings") return "holdings";
  // Composite, because this string IS the identity every schema cache key and
  // saved-layout key downstream is built from: two dataset views sharing the
  // bare kind would read each other's columns. Never show it to a person --
  // use `viewConfigCollectionLabel`.
  if (config.type === "dataset") return `dataset:${config.datasetId}`;
  if (config.type === "activity") return "activity";
  if (config.type === "document_review") return "document_review";
  if (config.type === "process") return config.view.kind;
  return config.type === "search" ? config.entityKind : config.panelKind;
}

// Everything the grid needs to know about a process view. The feature that
// owns the view builds one and passes it in its ViewConfig.
export interface ProcessView {
  // The schema kind: schema caches and saved layouts are keyed by it.
  kind: string;
  schema: RmsSchema;
  searchEndpoint: string;
  countEndpoint: string;
  // Headers every read of the view sends, read at request time.
  headers: () => Record<string, string>;
  // The columns the grid opens with, read when it seeds them.
  defaultColumns: () => readonly string[];
  // Cells every row carries whatever columns are visible.
  companionCells: readonly string[];
  defaultSort: { column: string; order: "asc" | "desc" }[];
  // Default widths by column name. They are minimums: text columns still
  // take the pane's slack.
  columnWidths: Record<string, number>;
  // The noun the count names the rows with.
  countLabel: string;
  nameColumnLabel: string;
  icon: Icon;
  // The name cell is a button that opens the row.
  openRow: (row: { id: string; name: string }) => void;
  // The name alone: no leading glyph and none of the record grid's
  // provenance badges (Imported, a note's kind, Private), since the view's
  // rows are its own, not the RMS's (the claims queue, #11422 round 18).
  namePlain?: boolean;
}

// What to call a view's rows in copy ("No funds results found"). The schema
// kind for every view but a dataset, whose kind carries an internal id.
export function viewConfigCollectionLabel(config: ViewConfig): string {
  if (config.type === "dataset") return config.datasetName ?? "rows";
  return viewConfigSchemaKind(config);
}

// From: CREATE DOMAIN scalar_kind AS TEXT CHECK (VALUE IN (...))
export type ScalarKind = "string" | "number" | "boolean" | "date" | "address";

// Structured JSON stored for an `address`-typed fact.
export interface AddressValue {
  formatted: string;
  street?: string | null;
  suite?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  lat?: number | null;
  lng?: number | null;
  place_id?: string | null;
  validated: boolean;
  validation_verdict?: string | null;
  // PREMISE | SUB_PREMISE | ROUTE | OTHER | ...
  validation_granularity?: string | null;
  // A lookup asked Google and it could not place this address (#13126). The
  // one value the "could not be confirmed" marker shows on.
  lookup_failed?: boolean;
}

export type AddressComponent = "city" | "state" | "zip" | "country";

// ============= Schema Types =============

// Schema returned from GET endpoints - all fields are always present
export interface RmsSchema {
  facts: SchemaFact[];
  groups: SchemaGroup[];
  /**
   * The column a scope on this grid fixes, when it has one (#11426).
   *
   * Scoped to one record, that column holds the record on every row — the
   * page's own title repeated — so it does not render. The backend names it
   * because the scoping predicate is the only thing that knows which column
   * it is; nothing on this side infers it.
   */
  scope_fixed_column?: string | null;
  /**
   * Further columns a grid scoped to one record leaves out, because on the
   * record's own page they describe that record rather than the row: a
   * record's Tasks tab drops Record, Record kind and List (#13423
   * follow-up). Dropped under the same rule as `scope_fixed_column`.
   */
  scope_hidden_columns?: string[];
}

export interface SchemaGroup {
  id: number;
  name: string;
  position: number;
}

// In-place rename of an enum variant, sent alongside scalar_values on a schema
// update. Migrates entity facts from the old value to the new one instead of
// reading as a delete + add. Named *_value to match the backend (avoids `from`).
export interface EnumRename {
  from_value: any;
  to_value: any;
  // Fold from_value into the option to_value the field already has (#12229).
  // Without it the server refuses a rename onto an existing option.
  merge?: boolean;
}

// What an attribute MEANS to the system, from rms_attributes.designates
// (#11270). Behavior that depends on attribute meaning (the fund's manager,
// the person's role) keys off this, never off the attribute's name — orgs
// name their designated attribute anything ("Management Company").
//
// A person's role has two halves, the job title and the employer, and an org
// may keep them on one field or on two (#11392): `person_role` names the field
// holding the title (and the employer too, when there is only one field), and
// `person_employer` names the field holding the employer when it is separate.
//
// `person_email` names a field holding one of the person's email addresses
// (#13069). It is a designation several attributes of one organization
// may carry at once ("Email", "Email (Work)", "Email (Personal)"): every
// consumer reads the whole designated set. `person_phone` names a field
// holding one of the person's phone numbers (#11277), and several fields may
// carry it the same way. `document_password` names a manager, vehicle or
// company field holding a password the record's documents are protected with
// (#14151); several fields may carry it too. Every other designation has one
// holder per organization and record type.
export type AttributeDesignation =
  | "fund_manager"
  | "manages_vehicles"
  | "person_role"
  | "person_employer"
  | "person_email"
  | "person_phone"
  | "document_password";

/** Which of the aggregate kind's rows a column holds values for (#13370, round 35). */
export type AppliesTo = "both" | "documents" | "interactions";

export interface SchemaFact {
  id: string;
  name: string;
  // The attribute's designation, or null/undefined for the vast majority of
  // attributes that carry no special meaning. Only real entity attributes
  // ever carry one; builtin/derived facts are always null.
  designates?: AttributeDesignation | null;
  // Optional display label that overrides `name` in user-facing UI surfaces
  // (column headers, filter list, filter group label, popover titles).
  // When set, `name` stays canonical -- keep using `name` for any identity
  // operation that crosses the API boundary (filter, sort, groupBy lookups).
  display_name?: string | null;
  position?: number; // Position for ordering attributes (within its group)
  inverse_name: string | null;
  is_system_managed: boolean;
  // A system-managed attribute the user may still delete. Only Backstop's
  // today: deleting one records a suppression that stops the connector
  // re-provisioning it, which is how a client stops a synced field (#11718).
  is_connector_removable?: boolean;
  is_required: boolean;
  is_array: boolean;
  is_range: boolean;
  is_enum: boolean;
  is_hierarchical: boolean;
  // Whether facts on this attribute appear on the record they point at --
  // the relationships view and the RMS context sent to the answering agent.
  // Defaults on; orgs turn it off for links that are only meaningful in the
  // forward direction, e.g. "Created by" (#10776).
  show_backreferences?: boolean;
  ai_autofill: boolean;
  renew_after: string | null; // ISO 8601 duration
  // Fixed-vocabulary cadence for AI-filled fields (issue #4914). NULL when
  // ai_autofill is false. Required when ai_autofill is true; defaults to
  // 'never' so no org sees a surprise scheduled run.
  ai_cadence: AICadence | null;
  // Day-of-week (1..7 ISO) for weekly cadence; day-of-month (1..31) for
  // monthly (29-31 clamp to the month's last day). NULL for
  // never/one_time/daily/quarterly/annually.
  ai_cadence_day: number | null;
  scalar_type: ScalarKind | null;
  scalar_unit: string | null;
  scalar_values: any[] | null;
  // When set, this enum fact's options are loaded lazily from this API path
  // the first time its filter clause opens, instead of being shipped eagerly
  // in `scalar_values`. Used for the interaction Author filter (#10758), whose
  // distinct-authors query is too costly to compute on every schema load.
  values_endpoint?: string | null;
  enum_colors: Record<string, string> | null;
  enum_labels?: Record<string, string> | null;
  // Frontend-only annotation (never on the wire): present exactly when this
  // fact is its kind's STATUS field, carrying the org's status_semantics map
  // (stored value → semantic state). Paint sites read it through
  // `enumValueToneStyles` — judgement tones for status, categorical ramp for
  // everything else; org hexColor never reaches paint (#11426, round 5).
  status_states?: Record<string, string> | null;
  // Frontend-only annotation: value → the CATEGORY the value belongs to
  // (the org's tag categories). A chip derives its hue from the category
  // name via the token ramp, so "same category, same colour" holds without
  // an org hex reaching paint (#11426, round 5).
  category_names?: Record<string, string> | null;
  // Transient, save-time only: rename intents for enum variants. Set by the
  // attribute editor when a value is renamed in place; consumed by the schema
  // PUT (convertToAttributeDetail) and never populated from server responses.
  enum_renames?: EnumRename[] | null;
  // Transient, save-time only, like enum_renames: the stored option list the
  // editor edited from (#12227). The server keeps any option stored now but
  // absent from it — one a sync added while the editor was open — instead of
  // reading it as removed. Defaults to the cached field's values on the PUT.
  enum_base_values?: any[] | null;
  entity_types: EntityKind[] | null;
  description: string;
  default_scalar_value: any | null;
  default_entity_ids: string[] | null;
  group_id: number | null;
  // The version of an org field's definition, sent back with an edit or
  // delete so the server can refuse one made against an older version. Absent
  // on built-in columns, which the editor cannot change.
  version?: string | null;
  is_sortable?: boolean;
  // Whether the grid offers group-by / filters on this column (#13416).
  // Absent reads as true. A false hides the column from the Groups tab, the
  // filter panel, the column menu and the search bar's operators: the
  // backend cannot group or filter it so that the result matches the cell.
  is_groupable?: boolean;
  is_filterable?: boolean;
  // The aggregate (documents and interactions) kind only (#13370, round 35): which rows
  // hold a value in the column. Absent on every other kind.
  applies_to?: AppliesTo | null;
  // When true, the cell value is a polymorphic {kind,id,display} reference
  // and TablePanel should render it via PolymorphicCell. Set on synthesized
  // schemas (e.g. activity feed) where the column may hold entities of any
  // kind, documents, or interactions.
  is_polymorphic?: boolean;
  // Frontend-only annotation (never on the wire): this reference-array
  // column keeps the CHIP grammar despite rule 2's "links never chip"
  // (#11426 gaps-v6 item 11). Declared only by process surfaces whose
  // chips carry working affordances — today the document-review grid's
  // entity columns (purple is_new marker, affiliation tint, per-link
  // reasoning hover). Record collections leave it unset and render
  // relation runs (RelationRunCell).
  entity_chips?: boolean;
  // Provenance from the unified search response. Populated when the schema
  // is built from the unified shape (today: bootstrap + /rms/schema/{kind}
  // endpoints emit legacy SchemaFact directly with provenance attached) so
  // the write path (persistence.ts / adapters/writeAdapter.ts) can dispatch
  // on it. Optional — code paths that build SchemaFact by hand leave it unset.
  provenance?: Provenance | null;
  // The full ColumnDescriptor this fact was adapted from. Same provenance
  // origin; included so write code can read meta/type without reconstructing.
  column?: ColumnDescriptor | null;
}

// How an autofill run on one (entity, attribute) ended — the outcome column
// of rms_autofill_attempts as the record GET reports it.
export type AutofillOutcome =
  | "pending"
  | "filled"
  | "not_found"
  | "error"
  | "skipped";

export type AICadence =
  | "never"
  | "one_time"
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annually";

export const AI_CADENCE_LABELS: Record<AICadence, string> = {
  never: "Never",
  one_time: "One-time",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

// The operations a schema save sends: only what the user did. Each edit and
// delete names the version of the field it was made against, and the server
// refuses the save as a conflict when that field changed since.
interface SchemaChanges {
  creates: AttributeDetail[];
  updates: { id: string; version: string; payload: AttributeDetail }[];
  deletes: { id: string; version: string }[];
  // Placed before `before_id` in group `group_id` (null: ungrouped), or at
  // the group's end when `before_id` is null.
  moves: { id: string; group_id: number | null; before_id: string | null }[];
  group_creates: { name: string }[];
  group_renames: { id: number; from_name: string; to_name: string }[];
  // `field_ids`: the fields the user saw in the group, which the delete
  // ungroups; one filed into it since makes the delete a conflict.
  group_deletes: { id: number; field_ids?: string[] }[];
}

// One thing the user did to the schema. A save is a list of these and
// nothing else: there is no field list to send, so a change is saved only if
// the code that made it says so, and a field the user did not touch (a
// colleague's change, an edit whose own save is still in flight) is never
// sent. Build them with the helpers in `api.ts` (`editField`, `moveField`,
// ...), which carry the versions the server checks.
export type SchemaOp =
  | { op: "create"; field: SchemaFact }
  // `field` carries the version the edit was made on; `stored` is the field
  // as the edit started from (its options are the edit's base, #12227).
  | { op: "edit"; field: SchemaFact; stored: SchemaFact }
  | { op: "delete"; id: string; version: string }
  // Before `before_id` in group `group_id` (null: ungrouped), or at the
  // group's end when `before_id` is null.
  | {
      op: "move";
      id: string;
      group_id: number | null;
      before_id: string | null;
    }
  | { op: "create_group"; name: string }
  // `from_name`: the stored name the user saw; refused if someone else
  // renamed the group meanwhile.
  | { op: "rename_group"; id: number; from_name: string; to_name: string }
  // `field_ids`: the fields the user saw in the group, which the delete
  // ungroups; one filed into it since makes the delete a conflict.
  | { op: "delete_group"; id: number; field_ids: string[] };

interface AttributeDetail {
  name: string;
  inverse_name: string | null;
  scalar_type: ScalarKind | null;
  scalar_unit: string | null;
  scalar_values: any[] | null; // If present, field is enum
  enum_colors: Record<string, string> | null;
  enum_renames?: EnumRename[] | null; // Rename intents (see EnumRename)
  enum_base_values?: any[] | null; // Stored options edited from (#12227)
  entity_types: EntityKind[] | null;
  is_required: boolean;
  is_array: boolean;
  is_hierarchical: boolean;
  show_backreferences?: boolean;
  ai_autofill: boolean;
  renew_after: string | null; // ISO 8601 duration
  ai_cadence: AICadence | null;
  ai_cadence_day: number | null;
  description: string;
  default_scalar_value: any | null;
  default_entity_ids: string[] | null;
  group_id: number | null;
}

// ============= Unified search response =============
//
// The five RMS search endpoints (entities, interactions, documents,
// shadowbook, activity-log) all return SearchResponse{columns, rows}.
// On the frontend we adapt these back to legacy EntityRow[] in
// Data/searchResponseAdapter.ts so TablePanel and the existing cell
// renderers don't change. The write path uses ColumnDescriptor.provenance
// directly (see Search/TablePanel/persistence.ts).
//
// Backend mirrors: fp-backend/fp/rms/response.py.

export interface ScalarSpec {
  type: "string" | "number" | "boolean" | "date" | "address";
  unit?: string | null;
  values?: any[] | null;
  enum_colors?: Record<string, string> | null;
  enum_labels?: Record<string, string> | null;
  is_array?: boolean;
  is_hierarchical?: boolean;
  is_range?: boolean;
}

export interface EntityRefSpec {
  types: EntityKind[];
  is_array?: boolean;
}

export interface DocumentRefSpec {
  is_array?: boolean;
}

export interface CellType {
  scalar?: ScalarSpec | null;
  entity_ref?: EntityRefSpec | null;
  document_ref?: DocumentRefSpec | null;
}

export type Provenance =
  | { kind: "entity_fact"; attribute_id: number }
  | { kind: "entity_name" }
  | { kind: "interaction_field"; field: string }
  | { kind: "interaction_cell"; column_id: number }
  | { kind: "document_field"; field: string }
  | { kind: "document_cell"; column_id: number }
  // A cell in one of the org's configured holdings columns (#10835). Writes
  // back to the snapshot row the grid row came from.
  | { kind: "holdings_cell"; column_id: number }
  // A cell in one of the org's configured transaction columns (#10997). Writes
  // back to the transaction the grid row came from, from either view.
  | { kind: "transaction_cell"; column_id: number }
  // A cell of a column the organization added to a dataset (#12733). Writes
  // to the dataset's cells endpoint, keyed by the row's source key; the
  // columns the source fills stay `readonly`.
  | { kind: "dataset_cell"; column_id: number }
  | { kind: "derived"; reason?: string | null }
  | { kind: "readonly"; reason?: string | null }
  | { kind: "linked_entities"; entity_kind: string }
  // A task's own field (#11942 round 21): Status (status_key) and Due
  // (due_day) write through PATCH /work/tasks/{id}, the same call the task
  // drawer makes.
  | { kind: "task_field"; field: "status_key" | "due_day" }
  // A claims-queue fact a reviewer edits from the grid (#11422 round 11):
  // Status, Priority and Document Type write through the same review routes
  // the review screen uses (approve, reject, overturn, submit, label).
  | {
      kind: "review_field";
      field: ReviewField;
      // The Document Type cell's pair as the server holds it, per row, so
      // its picker opens on what the cell shows. Absent on the other two.
      scan_job?: string | null;
      form_type?: string | null;
    }
  // Activity-log Reviewed cell: editable per-row (set as a cell override on
  // still-staged rows). Checking the box approves the row's Target via
  // POST /rms/review/approve. `review_kind` selects the approval shape and
  // `target_public_id` is the Target's public id (blob id for documents).
  | {
      kind: "audit_review";
      review_kind: "entity" | "interaction" | "document";
      target_public_id: string;
    };

export interface ColumnMeta {
  position?: number | null;
  group_id?: number | null;
  description?: string | null;
  inverse_name?: string | null;
  is_system_managed?: boolean;
  is_required?: boolean;
  is_sortable?: boolean;
  is_groupable?: boolean;
  is_filterable?: boolean;
  applies_to?: AppliesTo | null;
  ai_autofill?: boolean;
  ai_cadence?: AICadence | null;
  ai_cadence_day?: number | null;
  renew_after?: string | null;
  default_scalar_value?: any | null;
  default_entity_ids?: string[] | null;
}

export interface ColumnDescriptor {
  name: string;
  // Omitted iff per-row polymorphic — every row carries its own
  // CellType via override.
  type?: CellType | null;
  provenance: Provenance;
  meta: ColumnMeta;
}

export interface CellValue {
  scalar?: any;
  // A numberlike number fact (#10991): the high end of a range and the note
  // that followed the numbers. Absent or null on every other fact.
  scalar_end?: number | null;
  scalar_note?: string | null;
  entity?: ShallowEntity | null;
  document?: { id: string; filename: string } | null;
  // Autofill provenance for scalar (single-value) facts. Array facts
  // carry these per element inside the legacy FactValue list that
  // rides on `scalar`. See backend CellValue. Issue #9534.
  query_id?: string | null;
  workspace_id?: string | null;
  // Chat/agent-write provenance for scalar facts, split the same way
  // (#11499, #11677). Present only on values an agent wrote — see the
  // backend CellSource for why it's one nested object.
  source?: CellSource | null;
}

export interface CellSource {
  conversation_id: string;
  turn_id?: string | null;
  viewable?: boolean | null;
  authorized_by?: string | null;
  product?: "chat" | "agents" | null;
  agent_id?: string | null;
  run_id?: string | null;
  session_id?: string | null;
  // The first document the agent cited, when the viewer may read it (#14584).
  document_id?: string | null;
  document_name?: string | null;
  page_num?: number | null;
  interaction_id?: string | null;
}

export interface CellOverride {
  type?: CellType | null;
  provenance?: Provenance | null;
  ref?: {
    row_kind: "entity" | "interaction" | "document";
    row_id: string;
  } | null;
  // Entity kind of a deleted target restored from its audit snapshot
  // (#10775). The cell is a non-navigable scalar (no `ref`); this carries
  // the kind so the tombstone can render the matching icon.
  deleted_kind?: string | null;
  staging?: boolean | null;
  reason?: string | null;
}

export interface RowCell {
  value: CellValue;
  override?: CellOverride | null;
}

// HACK: only populated on /rms/activity-log/search rows. Carries audit-log
// fields the unified column structure doesn't capture. To be folded into
// the unified shape later. Search this comment to find the call sites.
export interface ActivityHack {
  fact_entries?: any[] | null;
  review_id?: string | null;
  bulk_operation_id?: string | null;
  bulk_target_count?: number | null;
  details_before?: any;
  details_after?: any;
  approved_by_display?: string | null;
  approved_at?: string | null;
  llm_reasoning?: string | null;
}

export interface UnifiedRow {
  id: string;
  keyset_id: string;
  name: string;
  staging?: boolean | null;
  group_rank?: number | null;
  // The full row count of this row's group; only when the request capped
  // rows per group (`group_row_limit`).
  group_size?: number | null;
  // The group this row is filed under, as a display string; null when
  // grouping is off. A multi-valued group axis (Author, #9968) fans a row
  // out into one occurrence per group, and every occurrence carries the
  // same cells — so only this names the group the row is actually in.
  group_value?: string | null;
  // The document lineage family the row is shown as part of, a key into
  // `UnifiedSearchResponse.lineages` (#13682); set only when the Versions
  // column is shown or grouped on. Grouped on Versions it is the row's
  // band (null in the no-versions band).
  lineage_id?: number | null;
  // The second level's group, as a display string, when the request grouped
  // that level by a bucket (a date's year, a type at a time; #13370).
  subgroup_value?: string | null;
  data: Record<string, RowCell>;
  activity_hack?: ActivityHack | null;
  // The polymorphic kind of the row's object ("document" / "interaction"),
  // surfaced only by the document-review route so the Name cell can route a
  // click to the right viewer. `id` carries the matching object id.
  object_kind?: string | null;
  // The originating source of a transactions row ("manual" / "caissa" / …),
  // surfaced only by the transactions search route so the grid can offer
  // edit/delete on the manually-entered rows alone (#10904).
  source?: string | null;
}

export interface UnifiedSearchResponse {
  columns: ColumnDescriptor[];
  rows: UnifiedRow[];
  // The lineage families the page's rows name, each once, keyed by lineage
  // id, with the members the viewer may open (#13682). Present only when the
  // Versions column is shown or grouped on.
  lineages?: Record<string, DocumentLineage> | null;
  // The holdings search's totals of its number and percentage columns over
  // every matched row, by column name (#14991). Absent on every other search.
  column_totals?: Record<string, number> | null;
}

// ============= Entity Read Types =============

export interface ShallowEntity {
  id: string;
  kind: EntityKind;
  name: string;
  display_name?: string | null;
  reasoning?: string | null;
  // True when the *entity itself* is AI-created and pending review
  // (its own rms_entities.review_id). Distinct from `link_staging`
  // below — both flags can be true, false, or one of each. Defaults
  // to false on rows from older API versions.
  staging?: boolean;
  // True when the *link* from the current surface (interaction
  // participant row, fact cell, backreference) to this entity is
  // AI-suggested and pending review. Defaults to false on rows from
  // older API versions and on surfaces where no link concept exists
  // (e.g. an entity-detail page rendering the entity's own card).
  // Drives the pending-review tooltip matrix in InlineEntityCard.
  // Issue #9728.
  link_staging?: boolean;
  // True when this entity was newly created during the linked object's
  // processing (vs matched to a pre-existing one). Only the document-review
  // grid populates it; drives the color-coded "new" chip + the row-expand's
  // new/matched marker. Distinct from `staging` (pending review) — an entity
  // can be new-but-already-approved or matched-but-still-staging. #10243.
  is_new?: boolean | null;
  // True when this person belongs to the client organization itself rather
  // than being an outside contact, so their chip renders gold (#10859). Only
  // person entities carry it; every other kind leaves it null. A person in an
  // org that has configured neither signal is false, and renders as before.
  is_internal?: boolean | null;
  // The company a person's current role points at, resolved by the server
  // through the org's employer/role designation (#11962). Only the quick
  // search populates it, for persons; a person with no role, and every other
  // kind, leaves it null or absent.
  employer?: string | null;
  // The person's current job title, the title half of the organization's
  // role designation (#14412). Only the quick search populates it, for
  // persons.
  role_title?: string | null;
}

export interface ShallowSearchResult {
  entity: ShallowEntity;
  facts: Record<string, EntityCellValue | null> | null;
}

export interface ShallowSearchResponse {
  results: ShallowSearchResult[];
  show_create_button: boolean;
  create_button_name?: string | null;
}

// The fact whose value drove per-record visibility on this record (#10968).
// Only present when the org configured conditional facts AND this record's
// value matched a rule; carries the "why" for a fact marked not applicable.
export interface ConditionalController {
  name: string;
  value: string;
}

// Full entity returned from GET /entities/{kind}/{id}.
// Documents and interactions are lazy-loaded via dedicated endpoints
// (see issue #9657); they are not included on this payload.
export interface EntitySchema extends ShallowEntity {
  facts: Fact[];
  backreferences: BackreferenceGroup[];
  aliases: string[];
  deleted_aliases: string[];
  groups: SchemaGroup[];
  conditional_controller?: ConditionalController | null;
}

// One bucket of the second grouping attribute, nested inside its parent
// group (#11141). `group_name` is the raw value the documents endpoint takes
// back as `subgroup=`; the empty string is the "no value" bucket.
export interface EntityDocumentSubgroup {
  group_name: string;
  label: string;
  count: number;
  most_recent_uploaded_at?: string | null;
}

export interface EntityDocumentGroup {
  group_name: string;
  // Display name for `group_name`, which is a raw value (a document-type key,
  // a custom-column value) for every grouping attribute except the document
  // group, where the two are the same string.
  label?: string;
  count: number;
  // ISO timestamp of the newest doc in this group; lets the dashboard
  // render the calendar+date indicator on collapsed accordion sections
  // without having to fetch the page first.
  most_recent_uploaded_at?: string | null;
  // Set on rows for vehicle-document groups (one per (vehicle, group)).
  vehicle_id?: string | null;
  vehicle_name?: string | null;
  // Present when the org configured a second grouping attribute. A group with
  // subgroups holds no document list of its own — its documents live under
  // the subgroups.
  subgroups?: EntityDocumentSubgroup[];
}

export interface EntityDocumentGroupsResponse {
  groups: EntityDocumentGroup[];
}

export interface EntityDocumentsPage {
  documents: ShallowDocument[];
  next_cursor?: string | null;
}

export interface EntityVehicleDocumentsPage {
  documents: VehicleDocument[];
  next_cursor?: string | null;
}

export interface EntityInteractionsPage {
  interactions: ShallowInteraction[];
  next_cursor?: string | null;
  /** With `upcoming=true` only: how many upcoming meetings the record has
   *  in all, past this page (#12394, round 25). */
  upcoming_total?: number | null;
}

// Base fact definition — the shared interface for both RMS schema facts
// and custom document/interaction attributes
interface FactDefinition {
  // Stringified rms_attributes.id. Present on facts served by the
  // entity GET (RMS schema attributes). Custom document/interaction
  // attributes synthesized client-side from other sources may omit it.
  id?: string;
  name: string;
  // What the attribute MEANS to the system, mirrored from SchemaFact -- see
  // the comment there. Absent from the entity GET's fact payload, which
  // carries values rather than attribute metadata, so a caller rendering a
  // record's own facts resolves it from that kind's schema and merges it in.
  // Behavior keyed on attribute meaning switches on this, never on `name`.
  designates?: AttributeDesignation | null;
  // Optional display override for user-facing labels. Mirrors the field on
  // SchemaFact -- see comment there.
  display_name?: string | null;
  // True when this fact is not applicable to this particular record, per the
  // org's conditional-facts config (#10968) -- e.g. "Funding Notes" on a
  // drawdown fund. Display-only: the fact is still in the payload with its
  // value, and `name` remains the identity key. The Facts tab drops the empty
  // ones and annotates the populated ones rather than discarding data.
  //
  // Distinct from the org's `factsHiddenFields`, which hides a field across
  // every record; this varies record by record with the
  // controller fact's value.
  hidden?: boolean;
  is_hierarchical: boolean;
  is_required: boolean;
  scalar_type: ScalarKind | null;
  scalar_unit: string | null;
  scalar_values: any[] | null;
  enum_colors: Record<string, string> | null;
  enum_labels?: Record<string, string> | null;
  // Frontend-only tone annotations (#11426, round 5) — see SchemaFact.
  status_states?: Record<string, string> | null;
  category_names?: Record<string, string> | null;
  entity_types: EntityKind[] | null;
  group_id?: number | null;
  // User-authored help text; rendered as the input placeholder so each
  // field is self-documenting. Empty string when not set.
  description?: string | null;
  // AI-autofill columns mirrored from rms_attributes. Optional on
  // FactDefinition so custom document/interaction attributes (which
  // don't live in rms_attributes) can omit them.
  ai_autofill?: boolean;
  ai_cadence?: AICadence | null;
  ai_cadence_day?: number | null;
  // True when an AI autofill run for this (entity, attribute) is
  // currently in flight on the backend. Set from rms_autofill_attempts
  // rows with outcome='pending' within the 30-minute staleness cutoff.
  // Seeds useAutofillStatusStore on page load so the pending chip
  // appears on cold reloads and across users on the same entity page
  // (issue #4914).
  autofill_pending?: boolean;
  // How the most recent autofill run on this (entity, attribute) ended,
  // whatever it was, or null when none has run. The record page reads it
  // for an attribute it marked pending once the server stops reporting
  // the run in flight, so a run whose websocket event was lost (page
  // reloaded, socket down) still ends with the row saying how it ended
  // (#12035).
  autofill_last_outcome?: AutofillOutcome | null;
  // When that run was attempted (ISO instant). The page compares it with
  // the value it held when it dispatched a run: until the run's own row
  // lands, the record still describes the previous run.
  autofill_last_outcome_at?: string | null;
  // Where the value lives / how it writes — copied from SchemaFact so
  // renderers can key presentation off first-class fields (the
  // interaction-type field chips as a tag taxonomy, #11426 gaps-v2).
  provenance?: Provenance | null;
}

// Convert a full SchemaFact (from the API) to FactDefinition (for rendering)
export function schemaFactToDefinition(fact: SchemaFact): FactDefinition {
  return {
    name: fact.name,
    designates: fact.designates,
    display_name: fact.display_name,
    is_hierarchical: fact.is_hierarchical,
    is_required: fact.is_required,
    scalar_type: fact.scalar_type,
    scalar_unit: fact.scalar_unit,
    scalar_values: fact.scalar_values,
    enum_colors: fact.enum_colors,
    enum_labels: fact.enum_labels,
    status_states: fact.status_states,
    category_names: fact.category_names,
    entity_types: fact.entity_types,
    group_id: fact.group_id,
    description: fact.description,
    // Renderers key one presentation decision off provenance: the
    // first-class interaction-type field is a tag taxonomy (its values chip
    // in table cells — #11426, gaps-v2) whether or not the org annotated
    // colors onto this surface's schema.
    provenance: fact.provenance,
  };
}

// Helper for user-facing labels: prefer display_name when set, else name.
// Use this anywhere a fact's label is rendered for humans -- never use it
// for filter/sort/groupBy identity (those must use `fact.name`).
/**
 * The interactions collection's TYPE DISCRIMINATOR — the first-class
 * interaction-type field, identified by provenance rather than by name
 * (#11426, round 10). One definition: the value-hued chip rule, the
 * default-column prune, and the footer's constancy clause all key off it,
 * and a local copy is exactly how two surfaces once disagreed.
 */
export function isInteractionTypeFact(fact: {
  provenance?: Provenance | null;
}): boolean {
  return (
    fact.provenance?.kind === "interaction_field" &&
    fact.provenance.field === "meeting_type"
  );
}

export function factDisplayName(fact: {
  name: string;
  display_name?: string | null;
}): string {
  return fact.display_name ?? fact.name;
}

// A prose column: one free-text value that may span lines (a summary, a
// reasoning note). The cell previews it on hover, the board card gives it
// the multi-line row, and a paste into it keeps a multi-line clipboard as
// ONE value (#11846). One predicate, so the three cannot drift apart.
export function isProseFact(fact: {
  is_array: boolean;
  scalar_unit: string | null;
}): boolean {
  return !fact.is_array && fact.scalar_unit === "multiline";
}

// Discriminated union for facts - based on is_array from schema
export type Fact = ScalarFact | ArrayFact;

interface ScalarFact extends FactDefinition {
  scalar: any | null;
  entity: ShallowEntity | null;
  // A numberlike number fact (#10991): the high end of a range and the note
  // that followed the numbers. Null on every other fact.
  scalar_end?: number | null;
  scalar_note?: string | null;
  staging?: boolean;
  // Query id of the AI-autofill extraction that produced this fact, if any.
  // Links to a query_answers doc the source-icon click can render in the
  // section-answer modal (issue #4914).
  queryId?: string | null;
  // Workspace id of the rms_autofill workspace the extraction ran in.
  // Paired with queryId to open the matrix AnswerDialog. Null for manual
  // facts or when the attempts row is missing.
  workspaceId?: string | null;
  // Agent-write provenance (#11499): the chat conversation/turn whose
  // confirmed request wrote this value. Null for every other writer.
  // sourceViewable says whether the VIEWING user can open the source —
  // the chip is a link when true, an attributed tooltip when false.
  // Facts written from a named-agent run also carry the run-view target
  // public ids so the chip links into the agents product.
  sourceConversationId?: string | null;
  sourceTurnId?: string | null;
  sourceViewable?: boolean | null;
  sourceAuthorizedBy?: string | null;
  // Stated by the backend — never inferred from which ids are non-null.
  sourceProduct?: "chat" | "agents" | null;
  sourceAgentId?: string | null;
  sourceRunId?: string | null;
  sourceSessionId?: string | null;
  // The first document the agent cited for this value, when the viewer may
  // read it (#14584): the Source chip opens it on the page. The interaction
  // id rides along for an email or meeting document.
  sourceDocumentId?: string | null;
  sourceDocumentName?: string | null;
  sourcePageNum?: number | null;
  sourceInteractionId?: string | null;
}

interface ArrayFact extends FactDefinition {
  array: FactValue[];
}

// A reference to a document (not an RMS entity — documents live in blobs, not rms_entities)
export interface DocumentRef {
  id: string;
  name: string;
  blob_kind?: string;
}

// A polymorphic cell value as emitted by the compiler's PolymorphicSelect —
// the cell can be an entity, document, or interaction, and the kind tag
// drives the render path. Used by the activity feed view.
export interface PolymorphicCellValue {
  kind: string;
  id: string;
  display: string | null;
  // The referenced row was hard-deleted; the value was restored from the
  // audit snapshot (#10775). Renders grayed-out and non-navigable.
  deleted?: boolean;
}

// Both scalar and entity can be non-null for composite facts
/** The claims-queue facts a reviewer edits from the grid (#11422 round 11). */
export type ReviewField = "status" | "priority" | "document_type";

/**
 * What a `review_field` cell writes (#11422 round 11). A label carries no
 * priority: the claim's own flag goes with it, read from the claim.
 */
export type ReviewFieldWrite =
  | { kind: "approve" }
  | { kind: "overturn" }
  | { kind: "return" }
  | { kind: "reject"; reason: string; note: string | null }
  | { kind: "label"; scan_job: string; form_type: string | null };

export interface FactValue {
  scalar: any | null;
  entity: ShallowEntity | null;
  // A numberlike number fact (#10991): the high end of a range and the note
  // that followed the numbers. Null on every other fact.
  scalar_end?: number | null;
  scalar_note?: string | null;
  // The verdict a `review_field` cell is written with (#11422 round 11): its
  // editor knows the transition, the reason and the note, which a bare status
  // or label value cannot carry. Absent everywhere else.
  review_write?: ReviewFieldWrite | null;
  document?: DocumentRef | null;
  // True when this specific value (or position within an array attribute)
  // is AI-originated and not yet reviewed.
  staging?: boolean;
  // Set on polymorphic columns (see SchemaFact.is_polymorphic). Only used
  // by read-only views; ignored by the editable cell pipeline.
  polymorphic?: PolymorphicCellValue | null;
  // Set on the activity feed's "Fact" and "New value" columns for
  // update-fact rows: an ordered list of per-attribute before/after pairs
  // parsed from details_before/details_after.facts. Triggers the activity-
  // fact inline renderer and the fact-changes modal. Ignored elsewhere.
  fact_entries?: ActivityFactEntry[];
  // Target entity kind for the activity row, used to look up the real
  // SchemaFact for each fact_entries[i].fact_key in the RMS bootstrap
  // (so enums, units, hierarchical, is_array, etc. render with the same
  // renderers the entity grids use). Null when the audit row's target is
  // not an RMS entity kind (e.g. documents).
  activity_target_kind?: EntityKind | null;
  queryId?: string | null;
  workspaceId?: string | null;
  // Agent-write provenance (#11499): see ScalarFact.sourceConversationId.
  sourceConversationId?: string | null;
  sourceTurnId?: string | null;
  sourceViewable?: boolean | null;
  sourceAuthorizedBy?: string | null;
  sourceProduct?: "chat" | "agents" | null;
  sourceAgentId?: string | null;
  sourceRunId?: string | null;
  sourceSessionId?: string | null;
  sourceDocumentId?: string | null;
  sourceDocumentName?: string | null;
  sourcePageNum?: number | null;
  sourceInteractionId?: string | null;
  // Note: Both can be populated for composite facts (e.g., "owns 50% of Company X")
}

// One entry in FactValue.fact_entries — a single attribute's before/after
// pair on an activity-log update-fact row. Values are kept in the raw
// FactValue shape (scalar + entity + optional entity_display info) so
// the existing cell renderers can display them verbatim.
export interface ActivityFactEntry {
  fact_key: string;
  // EntityCellValue shape (FactValue for scalar attrs, FactValue[] for
  // is_array attrs) so renderCell can render arrays as chips and singles
  // as inline values. Null when the side (before or after) is absent.
  before: EntityCellValue | null;
  after: EntityCellValue | null;
  // True when the audit payload stored the fact as literal JSON null
  // (as opposed to omitting the key or storing {value: {entity: null,
  // scalar: null}}). Signals a deliberate clear action — rendered as
  // "(cleared)" so users can distinguish it from "no value / unknown".
  cleared_before: boolean;
  cleared_after: boolean;
  // Scalar-shape inference used as a fallback when the target's schema
  // is missing or the attribute was removed after the audit row was
  // written. Real SchemaFact (when found) takes precedence at render time.
  scalar_type: ScalarKind | null;
}

// Backreference types
export interface BackreferenceGroup {
  name: string;
  forward_name: string;
  // The forward attribute's designation (see AttributeDesignation), or
  // null. The backend reads the designation to resolve the employer
  // backreference's title (#11392); the frontend no longer keys any display
  // on it (#14370).
  designates?: AttributeDesignation | null;
  scalar_type: ScalarKind | null;
  entity_types: EntityKind[] | null;
  entities: BackreferencedEntity[];
}

// Same as FactValue - both can be populated for composite facts
interface BackreferencedEntity {
  scalar: any | null;
  entity: ShallowEntity | null;
  // True when the source fact (pointing at this entity from another one)
  // is still pending review.
  staging?: boolean;
  // Note: Both can be populated for composite facts
}

// Field review surfaced on an interaction when individual fields are
// staged rather than the whole interaction.
export interface StagedField {
  field_name: string;
  staging: boolean;
}

// Related data types
interface ShallowInteraction {
  id: string;
  meeting_type: string;
  title: string;
  occurred_at: string; // ISO 8601 datetime
  // When it happened, in the temporal wire contract (#11055). Read this rather
  // than `occurred_at`: the bare timestamp cannot say whether the source stated
  // a time at all, and reading it in the viewer's zone puts a meeting held in
  // Hawaii on the wrong day for a reader in New York.
  occurrence: Temporal;
  // Provenance label ("Notetaker", "PDF", "Email", …) for the imported-
  // content badge (#10554).
  source?: string | null;
  // A meeting logged from a calendar that has not happened yet (#12394,
  // round 8): shown apart from the interactions that did, never as one.
  upcoming?: boolean;
  // A logged meeting whose invite was cancelled after someone wrote on it
  // (round 10): never upcoming, listed among the past rows at its date.
  cancelled?: boolean;
  // Its join link, sent for an upcoming meeting only.
  meeting_url?: string | null;
  // A meeting logged from a shared calendar (#12394, round 9b): everyone in
  // the organization sees it, so an upcoming group says so instead of "only
  // you".
  shared_calendar?: boolean;
  primary_participants: ShallowEntity[];
  mentioned_entities: ShallowEntity[];
  passing_mentions: ShallowEntity[];
  staging?: boolean;
  staged_fields?: StagedField[];
}

/** What a meeting logged from a connected calendar knows about itself. */
export interface CalendarMeeting {
  provider: string | null;
  owner_name: string | null;
  owner_email: string | null;
  started_at: string;
  ended_at: string | null;
  /** The invite's body as the organiser wrote it, while the events cache
   *  holds the event; null otherwise. */
  invite_description: string | null;
  meeting_url: string | null;
  /** The shared calendar it was logged from (#12394, round 9b), as the
   *  Shared calendars list names it; null for a personal calendar's row. */
  shared_calendar_name?: string | null;
}

// Full interaction types for detailed views
interface InteractionBase {
  title: string;
  meeting_type: string;
  occurred_at: string; // ISO 8601 datetime
  // When it happened, in the temporal wire contract (#11055). The occurrence is
  // the one value in the app rendered in its *own* zone rather than the
  // reader's: a meeting's local time is part of what the meeting was.
  occurrence: Temporal;
  primary_participants: ShallowEntity[]; // Changed from single primary_entity
  document_id: string | null;
  document_ids: string[]; // Multiple document relationships - just IDs
  document_filenames: Record<string, string>; // blob_id -> display filename
  document_blob_kind: string | null; // blob_kind of document_id
  // Provenance label ("Notetaker", "PDF", "Email", …) for the imported-
  // content badge (#10554).
  source?: string | null;
  summary: string | null;
  notes: string | null;
  enhanced_notes: string | null; // AI-generated notes from meeting transcript
  // True when a user has hand-edited the AI notes. Regenerate warns before
  // overwriting them. (#10095)
  user_modified_enhanced_notes?: boolean;
  // AI Notes lock: when true, enhanced notes are read-only and excluded from
  // regeneration until unlocked. locked_by is a display name. (#9902)
  enhanced_notes_locked?: boolean;
  enhanced_notes_locked_by?: string | null;
  enhanced_notes_locked_at?: string | null;
  calendar_link_available?: boolean;
  participant_emails?: string[];
  // The subset of participant_emails at one of the organization's own domains
  // (#10859). Unresolved attendees have no entity to classify, so their
  // fallback chips colour from this list instead of `is_internal`.
  internal_participant_emails?: string[];
  participant_names?: string[];
  is_group_note?: boolean;
  // The public id of the group note this is a personal note under; null for
  // a group note and every other interaction. The page redirects a personal
  // note to its group note, where the group vs personal toggle is (#15296).
  group_note_id?: string | null;
}

interface InteractionCustomColumn {
  id: number;
  name: string;
  data_type: "string" | "number" | "percentage" | "boolean" | "date" | "entity";
  description?: string | null;
  // The interaction editor renders from this payload, so what the API sends
  // has to be declared here or the editor silently falls back to a
  // single-value control: `is_enum` for a dropdown (#10957), `is_array` for a
  // multi-value column.
  is_enum?: boolean;
  scalar_values?: string[] | null;
  enum_colors?: Record<string, string> | null;
  entity_types?: string[] | null;
  is_array?: boolean;
}

interface InteractionResponse extends InteractionBase {
  id: string;
  // Every author (#9968): a notetaker group note is authored by its creator
  // plus everyone who recorded on it. `platform_author` stays the single
  // creator — it's the ownership key, not a display value.
  author: string[];
  platform_author: string | null;
  mentioned_entities: ShallowEntity[];
  passing_mentions: ShallowEntity[];
  custom_fields?: Record<number, any>;
  custom_columns?: InteractionCustomColumn[];
  has_multiple_meetings: boolean;
  /** Set on a meeting logged from a connected calendar (#12394). */
  calendar_meeting?: CalendarMeeting | null;
  /** Whether the viewer may write this interaction (#12394): a logged
   *  meeting is readable by the organization but written only by its
   *  calendar owner, so the page hides its edit controls otherwise. */
  can_write?: boolean;
  /** A meeting logged from a calendar that has not happened yet (#12394,
   *  round 8): the page says so, and offers prep notes. */
  upcoming?: boolean;
  /** A logged meeting whose invite was cancelled after someone wrote on it
   *  (round 10): kept with its notes, never upcoming, counted nowhere. */
  cancelled?: boolean;
  /**
   * Notetaker recording lifecycle: "pending" | "recording" | "abandoned" |
   * "recorded" | "processed". Null for non-notetaker interactions.
   * "pending"/"recording" mean a meeting is actively being recorded and has no
   * notes content yet. "abandoned" is terminal: the session was started, never
   * captured any audio, and was never stopped — it is NOT in progress (#10814).
   */
  notetaker_status?: string | null;
  /**
   * The meeting's scheduled end, when a calendar stated one; null for a Quick
   * Note. Tells a live meeting from a note opened later for one that is over
   * (#12275).
   */
  ended_at?: string | null;
  transcript?: string | null;
  /** True when the interaction row itself is AI-originated and pending. */
  staging?: boolean;
  /** Per-field staging (e.g. AI-written title/summary/occurred_at not yet reviewed). */
  staged_fields?: StagedField[];
  /** Subset of document_ids whose attachment relation row is still staging. */
  staged_document_ids?: string[];
  /**
   * When set, the by-blob lookup was made against an older `.fp` blob that
   * has since been superseded — `document_id` is the *current* notes blob,
   * and this field holds the blob id the caller originally requested.
   * Renders the "showing newer version" banner in the FP note viewer (#9723).
   */
  superseded_from_blob_id?: string | null;
  /**
   * An email's thread: the files the thread's other emails carried, newest
   * email first, none of them in `document_ids` (#11479). `document_ids`
   * stays this message's own attachments. Absent or empty for an email with
   * no thread siblings and for every other interaction.
   */
  thread_attachments?: ThreadAttachment[];
}

/** A file another email in the same thread arrived on (#11479). */
export interface ThreadAttachment {
  document_id: string;
  filename: string | null;
  /** Public id of the email the file arrived on. */
  interaction_id: string;
  interaction_title: string | null;
  /** When that email happened, as a day or as an instant in its zone. */
  occurrence: Temporal | null;
}

export interface InteractionByBlobResponse extends InteractionResponse {
  blob_resolution: "primary" | "historical" | "attachment" | "copy";
}

interface InteractionCreatePayload {
  title: string;
  meeting_type: string;
  occurred_at: string; // ISO 8601 datetime
  // The occurrence with its granularity and zone (#11055). A new interaction is
  // normally the moment the user is sitting in, which `occurred_at` already
  // covers; this is what lets a draft state a day with no time, or a moment in
  // a zone other than the browser's.
  occurrence?: Temporal;
  primary_participant_ids: string[]; // Changed from single primary_entity
  document_id?: string | null;
  document_ids?: string[]; // Multiple document relationships - just IDs
  summary?: string | null;
  content?: string | null;
  notes?: string | null;
  mentioned_entity_ids?: string[];
}

interface InteractionUpdatePayload {
  title?: string;
  meeting_type?: string;
  occurred_at?: string; // ISO 8601 datetime
  // The occurrence with its granularity and zone intact (#11055). An editor
  // sends this instead of `occurred_at`, because writing back a bare instant
  // loses the tag and promotes a day the source stated into a moment nobody
  // did.
  occurrence?: Temporal;
  primary_participant_ids?: string[]; // Changed from single primary_entity
  document_id?: string | null;
  document_ids?: string[]; // Multiple document relationships - just IDs
  summary?: string | null;
  content?: string | null;
  notes?: string | null;
  // The stored notes `notes` was typed on (#12212). The server refuses the
  // save (NotesConflictError) when the notes have changed since.
  notes_base?: string;
  mentioned_entity_ids?: string[];
  custom_fields?: Record<number, any>;
}

interface ShallowDocument {
  id: string;
  blob_kind: string;
  filename: string;
  uploaded_at: string; // ISO 8601 datetime
  filesystem_path?: string[] | null;
  document_types?: string[] | null;
  // Explicit group membership (#11162). Null/absent = derive from types; a
  // list is exact membership; [] is explicitly ungrouped.
  document_groups_override?: string[] | null;
  // A bare YYYY-MM-DD calendar day, never a timestamp: it carries no zone and
  // renders identically for every viewer (#11055).
  document_date?: string | null;
  s3_hash?: string | null;
  // The lock state the Documents page shows (#6559): password-protected and
  // not yet processed carries the lock badge, so a locked file on an entity
  // page no longer passes for a processed one.
  is_password_protected?: boolean;
  task_status?: string | null;
  task_id?: string | null;
  // True when the entity↔document link row is AI-originated and still
  // pending review. The same blob may appear unstaged under another
  // entity — this is a property of the relationship, not the blob.
  staging?: boolean;
  document_summary?: string | null;
  created_by?: string | null;
  import_source?: string | null;
  custom_column_values?: Record<string, string>;
}

export interface VehicleDocument extends ShallowDocument {
  vehicle_id: string;
  vehicle_name: string;
}

// ============= Document Config Types =============

interface DocumentType {
  type_key: string;
  display_name: string;
  description: string;
  is_interaction: boolean;
  contains_performance_estimates: boolean;
  is_fund_adv: boolean;
  is_other: boolean;
  /** Whether the AI may assign this type when it types a document (#14958).
   *  Off: people and imports still assign it, and search still offers it. */
  ai_assignable: boolean;
}

interface DocumentGroup {
  id: number | null;
  group_name: string;
  position: number;
  document_types: string[];
}

interface DocumentConfig {
  document_types: DocumentType[];
  document_groups: DocumentGroup[];
}

// ============= Entity Write Types (Payloads) =============

export interface EntityCreationPayload {
  name: string;
  facts: Record<string, EntityCreationFact>;
}

// Discriminated based on whether field is array or scalar. Both
// variants may carry an OCC baseline (expected_value / expected_values)
// that the PATCH handler compares to the live DB row; mismatches come
// back as a 409 fact_conflicts error (issue #4914).
export type EntityCreationFact =
  | {
      value: EntityFieldValue;
      values?: never;
      expected_value?: EntityFieldValue;
      expected_values?: never;
    } // Scalar field
  | {
      values: EntityFieldValue[];
      value?: never;
      expected_value?: never;
      expected_values?: EntityFieldValue[];
    }; // Array field

// 409 response from the entity PATCH handler: each conflict describes
// a field whose current DB value diverged from the baseline the client
// sent. The frontend renders these as per-field "keep yours / use
// theirs" prompts.
export interface FactConflict {
  attribute_id: string;
  attribute_name: string;
  is_array: boolean;
  // Current DB value in FactValue shape (scalar form) or FactValue[]
  // (array form). Matches the shape the rest of the renderers consume.
  current:
    | { scalar: any | null; entity: string | null }
    | Array<{
        scalar: any | null;
        entity: string | null;
      }>;
}

export interface FactConflictResponse {
  conflicts: FactConflict[];
}

// When sending to backend, entity is a string ID
// BOTH scalar and entity can be populated for composite facts
export interface EntityFieldValue {
  scalar: any | null;
  entity: string | null; // Entity ID as string when sending
  // Echoed from the fact as read (#10991) so a save of an unrelated field
  // keeps a range fact intact; a fresh edit sends the typed text as `scalar`
  // and the server derives both from it. Present only when the fact carries
  // one, so every other fact's payload is what it always was.
  scalar_end?: number | null;
  scalar_note?: string | null;
  // Note: Both can be non-null for composite facts (e.g., "owns 50% of Company X")
}

// The range end and note of a numberlike fact (#10991), as the PATCH payload
// carries them: only when the fact has them.
export function numberlikeFields(value: {
  scalar_end?: number | null;
  scalar_note?: string | null;
}): Pick<EntityFieldValue, "scalar_end" | "scalar_note"> {
  const out: Pick<EntityFieldValue, "scalar_end" | "scalar_note"> = {};
  if (value.scalar_end !== null && value.scalar_end !== undefined) {
    out.scalar_end = value.scalar_end;
  }
  if (value.scalar_note) out.scalar_note = value.scalar_note;
  return out;
}

interface EntityUpdatePayload {
  name: string;
  facts: Record<string, EntityCreationFact | null>; // null means delete the fact
  aliases?: string[];
  deleted_aliases?: string[];
}

// ============= Error Response Types =============

interface ErrorEntitiesViolateRule {
  message: string;
  entities: ShallowEntity[];
}

interface ErrorSchemaInvalid {
  message: string;
  // The save was made against a schema someone else has changed since; the
  // editor offers to reload rather than showing the message as a failure.
  conflict?: boolean;
}

interface EntityReferencedError {
  message: string;
  referenced_by: ShallowEntity[] | ShallowInteraction[];
}

interface DeletionResult {
  num_affected: number;
}

// One group of inbound links a type change removes (#11700): the linking
// attribute (or document / interaction column), the kind of holder — a record
// type, or "document" / "interaction" for a column cell — how many, and one
// real holder the user can open.
interface InboundLinkGroup {
  attribute_name: string;
  host_kind: EntityKind | "document";
  count: number;
  example_id: string;
  example_name: string;
}

// What changing a record's type would do (#11700), from the backend
// planner. `carried` / `dropped` are attribute names with at least one fact
// that survives / goes; a partially compatible attribute (an enum whose
// values are only partly in the new option list) appears in both. `replaced`
// names attributes whose visible fact is discarded because the record already
// holds a fact under the new type's attribute of that name.
// `inbound_links_removed` is the sum of `inbound_links`.
interface KindChangePlan {
  source_kind: EntityKind;
  target_kind: EntityKind;
  carried: string[];
  dropped: string[];
  replaced: string[];
  inbound_links_removed: number;
  inbound_links: InboundLinkGroup[];
  performance_data_removed: boolean;
}

interface KindChangeResult extends KindChangePlan {
  id: string;
  name: string;
}

// ============= Filter Types =============

export type FilterOp =
  | "eq"
  | "in"
  | "between"
  | "gte"
  | "lte"
  | "prefix"
  | "prefix_many"
  | "similar"
  | "icontains"
  | "ref_eq"
  | "ref_in"
  // Filter an entity-link attribute by the linked record's type instead of
  // by specific records (#11674). Value is a list of entity kinds; only
  // offered on attributes whose entity_types allow more than one.
  | "ref_kind_in"
  // Address proximity: value is { lat, lng, radius_meters }. Backend
  // emits ST_DWithin against the generated rms_facts.geo column.
  | "near"
  // Blankness (#11243). Value-less, and exact complements of each other:
  // every record is one or the other. "Blank" is whatever the grid renders
  // as an em dash (see TablePanel/cellEmptiness.ts), which is broader than
  // "null" — an empty string, an empty list and an empty address all count.
  | "is_blank"
  | "is_not_blank";

// A clause carrying one of these constrains the attribute's presence rather
// than its value, so it holds no `value` and suppresses the value editor.
export const PRESENCE_OPS: readonly FilterOp[] = ["is_blank", "is_not_blank"];

export const isPresenceOp = (op: FilterOp): boolean =>
  PRESENCE_OPS.includes(op);

export interface AddressNearValue {
  lat: number;
  lng: number;
  radius_meters: number;
  // Display-only: the formatted center label so the UI can show what
  // the user picked without re-resolving the lat/lng.
  label?: string;
}

export interface FrontendFilter {
  name: string;
  op: FilterOp;
  value?: any;
  selected_entities?: ShallowEntity[]; // Display-only: entity info for chips
  // For address-typed attributes: the JSON sub-field being filtered on.
  component?: AddressComponent;
  // How this clause binds to the one before it in the ordered list
  // (#10851): "or" extends the previous OR group, "and" starts a new
  // one; groups AND together. Absent everywhere = legacy payload — the
  // backend groups by attribute name instead. Managed by the helpers in
  // Search/Filter/filterJoins.ts.
  join?: "and" | "or";
  // Top-level OR group (#10851): clauses sharing a group index form one
  // AND-of-ORs expression and the groups OR together. Absent = group 0;
  // only written once a second group exists, so single-group payloads
  // stay byte-identical to pre-group ones. Managed by filterJoins.ts.
  group?: number;
  // Invert the clause (#11421): "is any of A, B" becomes "is none of A, B".
  // Orthogonal to `op` rather than a negated twin of each one, so the one
  // flag covers every editor and a later operator picker can select
  // (op, negate) pairs. A record holding NO value for the attribute matches
  // an inverted clause, so a clause and its inversion partition the view.
  // Only ever written as `true` — un-inverting deletes the field, keeping
  // untouched payloads byte-identical to pre-negation ones.
  negate?: true;
}

// ============= Search Result Types =============

// Entity cell value - the raw value from the search API
// Must be interpreted with corresponding SchemaFact to understand structure
export type EntityCellValue =
  | FactValue // Single value (for non-array facts)
  | FactValue[]; // Array of values (for is_array facts)

export interface EntityRow {
  id: string;
  keyset_id: string;
  name: string;
  // Dictionary mapping from attribute ID (string) to values
  // Values must be interpreted using the corresponding SchemaFact
  data: Record<string, EntityCellValue>;
  // Per-cell provenance override, keyed by `column.name` (same key as
  // `data`). Populated by searchResponseAdapter when the unified response
  // carries a `RowCell.override.provenance` — i.e. when a view (today:
  // document_review) needs editability/write-routing to vary per row,
  // not just per column. Absent for every view that emits no override, so
  // the column-level provenance path is untouched. Read by isCellReadOnly
  // (editability) and the write path (persistFact dispatch).
  cellProvenance?: Record<string, Provenance>;
  /** True for synthetic rows representing files still being processed */
  _processing?: boolean;
  /** True while the row is being held on screen despite no longer matching
   * the active filter, because this user's own edit is what stopped it
   * matching (#11363). Set by the background-refetch merge, cleared when a
   * user-initiated result set arrives. Never sent by the server. */
  _retained?: boolean;
  /** True when the entity row itself is AI-originated and pending review. */
  staging?: boolean;
  /** DENSE_RANK of this row's group when the request used `group_by`.
   * Null when grouping is off. Rows that share a `group_rank` belong
   * to the same group and render under one header. */
  group_rank?: number | null;
  /** The full row count of this row's group when the request capped rows
   * per group (`group_row_limit`); the page may then hold fewer, and the grid
   * offers the rest. Absent when the page holds whole groups. */
  group_size?: number | null;
  /** The group this row is filed under, as a display string; null when
   * grouping is off. A multi-valued group axis (Author, #9968) fans a row
   * out into one occurrence per group; the occurrences are cell-identical,
   * so the header must read this rather than the row's own cell. */
  group_value?: string | null;
  /** The second level's group, as a display string, when the request grouped
   * that level by a bucket (`group_buckets`: a date's year, a type at a
   * time). Null otherwise (#13370, round 30). */
  subgroup_value?: string | null;
  /** Polymorphic kind of the row's object ("document" / "interaction"),
   * populated only on the document-review view. Drives the Name cell's
   * leading kind icon and which viewer a click opens. `id` is the object id
   * (blob id for documents, interaction public id for interactions). */
  object_kind?: string | null;
  /** Originating source of a transactions row ("manual" / "caissa" / …),
   * populated only on the transactions view. Manual rows get an edit/delete
   * kebab; connector rows stay read-only (#10904). */
  source?: string | null;
  /** The document lineage family the row is shown as part of (#13682), when
   * the Versions column is shown or grouped on: the Versions cell and band
   * read the row's place and the family's label from it. Absent for a row
   * in no family. */
  versions?: RowVersions;
}

/** A row's family for the Versions column (#13682). */
export interface RowVersions {
  /** The family's lineage id. */
  family: number;
  /** The family, with the members the viewer may open; null when the
   * response carries none for it (a read that sees rows the viewer's own
   * side map does not, as a superadmin's does). */
  lineage: DocumentLineage | null;
  /** Whether the page is grouped on Versions: the row then sits in its
   * family's band and the cell draws the family's rail. */
  banded: boolean;
}

// ============= Type Guards =============

// Helper type guards for discriminated unions
function isScalarFact(fact: Fact): fact is ScalarFact {
  return "scalar" in fact || "entity" in fact;
}

function isArrayFact(fact: Fact): fact is ArrayFact {
  return "array" in fact;
}

// Export all types
export type {
  // Schema types
  SchemaChanges,
  AttributeDetail,

  // Entity read types
  ScalarFact,
  ArrayFact,
  FactDefinition,
  BackreferencedEntity,
  ShallowInteraction,
  InteractionBase,
  InteractionResponse,
  InteractionCreatePayload,
  InteractionUpdatePayload,
  ShallowDocument,

  // Document config types
  DocumentType,
  DocumentGroup,
  DocumentConfig,

  // Entity write types
  EntityUpdatePayload,

  // Error types
  ErrorEntitiesViolateRule,
  ErrorSchemaInvalid,
  EntityReferencedError,
  DeletionResult,
  InboundLinkGroup,
  KindChangePlan,
  KindChangeResult,
};

// Export type guards
export { isScalarFact, isArrayFact };
